import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import SavingsRoundedIcon from '@mui/icons-material/SavingsRounded';
import WalletRoundedIcon from '@mui/icons-material/WalletRounded';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import ProaDatePicker from '../../../components/Common/ProaDatePicker';
import {
  createProjectVitalFinanceAccount,
  updateProjectVitalFinanceAccount,
} from '../services/projectVitalService';
import type {
  ProjectVitalFinanceAccount,
  ProjectVitalFinanceAccountType,
  ProjectVitalFinancialScope,
} from '../types/projectVital';

type Props = {
  open: boolean;
  onClose: () => void;
  financialScope?: ProjectVitalFinancialScope;
  account?: ProjectVitalFinanceAccount | null;
};

const ACCOUNT_TYPES: Array<{
  value: ProjectVitalFinanceAccountType;
  label: string;
  description: string;
  icon: React.ReactNode;
}> = [
  {
    value: 'cuenta',
    label: 'Cuenta',
    description: 'Cuenta bancaria habitual',
    icon: <AccountBalanceWalletRoundedIcon />,
  },
  {
    value: 'ahorro',
    label: 'Ahorro',
    description: 'Dinero reservado o ahorrado',
    icon: <SavingsRoundedIcon />,
  },
  {
    value: 'efectivo',
    label: 'Efectivo',
    description: 'Dinero disponible en metálico',
    icon: <WalletRoundedIcon />,
  },
  {
    value: 'otro',
    label: 'Otro',
    description: 'Otro tipo de saldo',
    icon: <PaymentsRoundedIcon />,
  },
];

function todayIso() {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000)
    .toISOString().slice(0, 10);
}

export default function ProjectVitalFinanceAccountDialog({
  open,
  onClose,
  financialScope = 'personal',
  account = null,
}: Props) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [name, setName] = useState('');
  const [accountType, setAccountType] =
    useState<ProjectVitalFinanceAccountType>('cuenta');
  const [openingBalance, setOpeningBalance] = useState('0');
  const [openingBalanceDate, setOpeningBalanceDate] = useState(todayIso());

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        name: name.trim(),
        account_type: accountType,
        financial_scope: account?.financial_scope ?? financialScope,
        opening_balance: Number(openingBalance.replace(',', '.')),
        opening_balance_date: openingBalanceDate,
      };

      return account
        ? updateProjectVitalFinanceAccount(account.id, payload)
        : createProjectVitalFinanceAccount(payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['project-vital', 'finance'],
      });

      enqueueSnackbar(
        account ? 'Cuenta actualizada.' : 'Cuenta creada.',
        { variant: 'success' },
      );

      onClose();
    },
    onError: () => {
      enqueueSnackbar(
        account
          ? 'No se pudo actualizar la cuenta.'
          : 'No se pudo crear la cuenta.',
        { variant: 'error' },
      );
    },
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    if (account) {
      setName(account.name);
      setAccountType(account.account_type);
      setOpeningBalance(String(account.opening_balance));
      setOpeningBalanceDate(account.opening_balance_date);
      return;
    }

    setName('');
    setAccountType('cuenta');
    setOpeningBalance('0');
    setOpeningBalanceDate(todayIso());
  }, [open, account]);

  const numericBalance = Number(openingBalance.replace(',', '.'));
  const isValid =
    Boolean(name.trim()) &&
    Boolean(openingBalanceDate) &&
    !Number.isNaN(numericBalance);

  return (
    <Dialog
      open={open}
      onClose={mutation.isPending ? undefined : onClose}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="sm"
      PaperProps={{
        sx: {
          bgcolor: '#111c2f',
          border: fullScreen
            ? 'none'
            : '1px solid rgba(148,163,184,0.16)',
          borderRadius: fullScreen ? 0 : 3.5,
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 850,
          borderBottom: fullScreen
            ? '1px solid rgba(148,163,184,0.10)'
            : 'none',
        }}
      >
        {account ? 'Editar cuenta' : 'Nueva cuenta'}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2.4} sx={{ pt: 1.5 }}>
          <TextField
            label="Nombre"
            placeholder="Ej. Cuenta principal"
            autoFocus
            fullWidth
            value={name}
            onChange={(event) => setName(event.target.value)}
            disabled={mutation.isPending}
          />

          <Box>
            <Typography
              sx={{
                mb: 1,
                color: '#94a3b8',
                fontSize: '0.78rem',
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: 0.8,
              }}
            >
              Tipo de cuenta
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, minmax(0, 1fr))',
                  sm: 'repeat(4, minmax(0, 1fr))',
                },
                gap: 1,
              }}
            >
              {ACCOUNT_TYPES.map((option) => {
                const selected = accountType === option.value;

                return (
                  <ButtonBase
                    key={option.value}
                    onClick={() => setAccountType(option.value)}
                    disabled={mutation.isPending}
                    sx={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      borderRadius: 2.5,
                    }}
                  >
                    <Box
                      sx={{
                        height: '100%',
                        p: 1.35,
                        borderRadius: 2.5,
                        border: `1px solid ${
                          selected
                            ? alpha('#4DD0E1', 0.55)
                            : alpha('#94a3b8', 0.13)
                        }`,
                        bgcolor: selected
                          ? alpha('#4DD0E1', 0.09)
                          : alpha('#ffffff', 0.018),
                        transition: 'all 0.15s ease',
                      }}
                    >
                      <Box
                        sx={{
                          color: selected ? '#4DD0E1' : '#64748b',
                          display: 'flex',
                          mb: 0.9,
                        }}
                      >
                        {option.icon}
                      </Box>

                      <Typography
                        sx={{
                          color: selected ? '#f8fafc' : '#cbd5e1',
                          fontWeight: 800,
                          fontSize: '0.82rem',
                        }}
                      >
                        {option.label}
                      </Typography>

                      <Typography
                        sx={{
                          mt: 0.25,
                          color: '#64748b',
                          fontSize: '0.66rem',
                          lineHeight: 1.35,
                        }}
                      >
                        {option.description}
                      </Typography>
                    </Box>
                  </ButtonBase>
                );
              })}
            </Box>
          </Box>

          <TextField
            label="Saldo inicial"
            type="number"
            fullWidth
            value={openingBalance}
            onChange={(event) => setOpeningBalance(event.target.value)}
            disabled={mutation.isPending}
            inputProps={{
              step: '0.01',
              inputMode: 'decimal',
            }}
          />

          <ProaDatePicker
            label="Fecha del saldo inicial"
            value={openingBalanceDate}
            onChange={setOpeningBalanceDate}
            disabled={mutation.isPending}
            required
            size="medium"
            helperText="Fecha de referencia del saldo. No se suman movimientos anteriores a ella."
          />
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: fullScreen
            ? 'calc(20px + env(safe-area-inset-bottom))'
            : 2.5,
          pt: 2,
        }}
      >
        <Button onClick={onClose} disabled={mutation.isPending}>
          Cancelar
        </Button>

        <Button
          variant="contained"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending || !isValid}
        >
          {mutation.isPending
            ? 'Guardando...'
            : account
              ? 'Guardar cambios'
              : 'Crear cuenta'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
